import { AlertTriangle } from "react-feather";

import { Button } from "./button";
import { Spinner } from "./spinner";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "./dialog";
import { cn } from "@/lib/utils";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  isLoading?: boolean;
  /** Highlighted name of the thing being acted on (e.g. the item to delete). */
  itemName?: string;
  /** Extra line under the message, e.g. "This action cannot be undone." */
  footnote?: string;
}

const VARIANT_STYLES = {
  danger: {
    icon: "text-destructive",
    iconBg: "bg-destructive/10",
  },
  warning: {
    icon: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
  },
  info: {
    icon: "text-blue-600 dark:text-blue-400",
    iconBg: "bg-blue-100 dark:bg-blue-500/15",
  },
} as const;

/**
 * The one confirm dialog. Rides ui/dialog (one scrim, one z-index) and
 * ui/button (token colors, dark-mode safe, focus ring).
 */
export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  isLoading = false,
  itemName,
  footnote,
}: ConfirmationModalProps) {
  const styles = VARIANT_STYLES[variant];

  return (
    <DialogRoot open={isOpen} onOpenChange={(open) => !open && !isLoading && onClose()}>
      <DialogContent>
        <DialogHeader showCloseButton onClose={onClose}>
          <span className="flex items-center gap-3">
            <span
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-lg",
                styles.iconBg,
              )}
            >
              <AlertTriangle className={cn("h-5 w-5", styles.icon)} />
            </span>
            {title}
          </span>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{message}</p>
        {itemName && (
          <p className="mt-2 rounded-md bg-muted px-3 py-2 text-sm font-medium text-foreground">
            "{itemName}"
          </p>
        )}
        {footnote && (
          <p className="mt-2 text-xs text-muted-foreground">{footnote}</p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            variant={variant === "danger" ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && <Spinner size="sm" className="border-current" />}
            {isLoading ? `${confirmText}...` : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
